import { useCallback, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { Roadwork, Task } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { UiModal } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"

const POPUP_WIDTH = 514
const POPUP_HEIGHT = 574

type RoadworkTaskEditModalProps = {
    className?: string,
    FormComponent: React.FC<any>,
    roadwork: Roadwork,
    task?: Task,
    mode?: "add" | "update",
    open: boolean,
    onClose: () => void,
}

export const RoadworkTaskEditModal = (props: RoadworkTaskEditModalProps) => {
    const {
        className,
        FormComponent,
        mode = "add",
        roadwork,
        task,
        open,
        onClose,
    } = props

    const [hasOngoingModifications, setHasOngoingModifications] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setAlert } = useAlertsDispatchContext()

    const disableTaskTypes = roadwork.tasks.filter(t => t.taskType !== task?.taskType).map(t => t.taskType)
    const localizedTasksAllowed = mode === "add" ? roadwork.tasks.length === 0 : roadwork.tasks.length <= 1

    const handleClose = useCallback((checkModifications = true) => {
        if (checkModifications && hasOngoingModifications) {
            setAlert({
                alertTitle: t(I18N_NAMESPACE, `containers.roadworkTaskEditModal.alert.title.${mode === "add" ? "add" : "update"}`),
                cancelText: t(I18N_NAMESPACE, "containers.roadworkTaskEditModal.alert.cancelButton"),
                confirmText: t(I18N_NAMESPACE, "containers.roadworkTaskEditModal.alert.confirmButton"),
                callback: () => {
                    setHasOngoingModifications(false)
                    onClose()
                },
                open: true,
            })
        } else {
            setHasOngoingModifications(false)
            onClose()
        }
    }, [hasOngoingModifications, t, onClose, setAlert, mode])

    return <UiModal
        className={className}
        height={POPUP_HEIGHT}
        width={POPUP_WIDTH}
        open={!!open}
        onClose={handleClose}
        showLoader={isSubmitting}
        title={t(I18N_NAMESPACE, `containers.roadworkTaskEditModal.title.${mode === "add" ? "add" : "edit"}`)}
    >
        <FormComponent
            sx={{ flex: "1 1 auto" }}
            onClose={handleClose}
            onSubmitting={(submitting: boolean) => setIsSubmitting(submitting)}
            onChange={setHasOngoingModifications}
            disableTaskTypes={disableTaskTypes}
            localizedTasksAllowed={localizedTasksAllowed}
            task={task}
            roadwork={roadwork}
        />
    </UiModal>
}
